import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useCallback, useEffect, useRef } from 'react';
import { components } from 'react-select';
import classNames from 'classnames/bind';
import { arrayOf, bool, func } from 'prop-types';
import useTouchDetection from "../../hooks/useTouchDetection";
import { CheckCircleFilled } from "../../icons";
import { canUseDOM } from "../../utils/canUseDOM";
import noop from "../../utils/noop";
import { truncateText } from "../../utils/string";
import Checkbox from '../../Checkbox';
import styles from '../Select.css';
import { selectPropsPropTypes, valuePropType } from '../shapes';
var cx = classNames.bind(styles);

var renderSuggestion = function renderSuggestion(option, _ref) {
  var isSelected = _ref.isSelected,
      isEllipsis = _ref.isEllipsis,
      label = _ref.label,
      optionCheckColor = _ref.optionCheckColor,
      iconRef = _ref.iconRef,
      classes = _ref.classes;
  return /*#__PURE__*/React.createElement(React.Fragment, null, option.left && /*#__PURE__*/React.createElement("span", {
    className: cx('Option_suggestion_left', classes.optionSuggestionLeft)
  }, option.left), /*#__PURE__*/React.createElement("span", {
    className: cx('Option__text', {
      ellipsis: isEllipsis
    }, classes.optionText)
  }, label), isSelected ? /*#__PURE__*/React.createElement("div", {
    className: cx('Option__icon', classes.optionIcon),
    style: {
      color: optionCheckColor
    },
    ref: iconRef
  }, /*#__PURE__*/React.createElement(CheckCircleFilled, {
    size: "md"
  })) : option.right && /*#__PURE__*/React.createElement("span", {
    className: cx('Option_suggestion_right', classes.optionSuggestionRight)
  }, option.right));
};

var renderDesktopOption = function renderDesktopOption(option, _ref2) {
  var isMulti = _ref2.isMulti,
      isSelected = _ref2.isSelected,
      isDisabled = _ref2.isDisabled,
      colorTheme = _ref2.colorTheme,
      onClickMultiple = _ref2.onClickMultiple,
      isEllipsis = _ref2.isEllipsis,
      label = _ref2.label,
      optionCheckColor = _ref2.optionCheckColor,
      iconRef = _ref2.iconRef,
      classes = _ref2.classes;
  return option.left || option.right ? renderSuggestion(option, {
    classes: classes,
    isSelected: isSelected,
    isEllipsis: isEllipsis,
    label: label,
    optionCheckColor: optionCheckColor,
    iconRef: iconRef
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, isMulti ?
  /*#__PURE__*/
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events
  React.createElement("div", {
    title: option.title,
    tabIndex: -1,
    role: "button",
    onClick: onClickMultiple,
    className: cx({
      disabled: isDisabled
    }, isEllipsis ? 'full-width' : '')
  }, /*#__PURE__*/React.createElement(Checkbox, {
    theme: colorTheme,
    checked: isSelected,
    onChange: noop,
    stretchText: isEllipsis,
    disabled: isDisabled
  }, /*#__PURE__*/React.createElement("span", {
    className: cx('Option__text', 'Option__text_multiple', {
      ellipsis: isEllipsis
    }, classes.optionText)
  }, label))) : /*#__PURE__*/React.createElement("span", {
    title: option.title,
    className: cx('Option__text', {
      ellipsis: isEllipsis
    }, classes.optionText)
  }, label));
};

var renderMobileOption = function renderMobileOption(option, _ref3) {
  var isSelected = _ref3.isSelected,
      isEllipsis = _ref3.isEllipsis,
      label = _ref3.label,
      optionCheckColor = _ref3.optionCheckColor,
      iconRef = _ref3.iconRef,
      classes = _ref3.classes;
  return option.left || option.right ? renderSuggestion(option, {
    classes: classes,
    isSelected: isSelected,
    isEllipsis: isEllipsis,
    label: label,
    optionCheckColor: optionCheckColor,
    iconRef: iconRef
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    title: option.title,
    className: cx('Option__text', {
      ellipsis: isEllipsis
    })
  }, label), isSelected && /*#__PURE__*/React.createElement("div", {
    className: cx('Option__icon'),
    style: {
      color: optionCheckColor
    },
    ref: iconRef
  }, /*#__PURE__*/React.createElement(CheckCircleFilled, {
    size: "md"
  })));
};

export var Option = function Option(props) {
  var data = props.data,
      innerRef = props.innerRef,
      isDisabled = props.isDisabled,
      isFocused = props.isFocused,
      isMulti = props.isMulti,
      isSelected = props.isSelected,
      selectOption = props.selectOption,
      options = props.options,
      _props$selectProps = props.selectProps,
      isMobile = _props$selectProps.isMobile,
      colorTheme = _props$selectProps.colorTheme,
      handleMenuClose = _props$selectProps.handleMenuClose,
      optionEllipsis = _props$selectProps.optionEllipsis,
      optionMaxTextLength = _props$selectProps.optionMaxTextLength,
      isAutoOptionHeight = _props$selectProps.isAutoOptionHeight,
      optionHeights = _props$selectProps.optionHeights,
      optionCheckColor = _props$selectProps.optionCheckColor,
      classes = _props$selectProps.classes;
  var optionWrapperRef = useRef(null);
  useEffect(function () {
    if (isAutoOptionHeight) {
      var index = options.findIndex(function (option) {
        return option.value === data.value;
      });

      if (!optionHeights[data.value] && canUseDOM && optionWrapperRef.current) {
        optionHeights[data.value] = {
          height: optionWrapperRef.current.offsetHeight,
          index: index
        };
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);
  var Component = isMobile ? 'div' : components.Option;

  var onClickMultiple = function onClickMultiple(e) {
    selectOption(_objectSpread({}, data));
    e.stopPropagation();
    e.preventDefault();
  };

  var onTouchEnd = useCallback(function (e, touched) {
    e.preventDefault();

    if (touched) {
      selectOption(_objectSpread({}, data));

      if (!isMulti) {
        handleMenuClose();
      }
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [data, isMulti]);

  var _useTouchDetection = useTouchDetection({
    condition: isMobile && !isDisabled,
    onEnd: onTouchEnd
  }),
      _useTouchDetection2 = _slicedToArray(_useTouchDetection, 1),
      touchEvents = _useTouchDetection2[0];

  var label = optionMaxTextLength ? truncateText(data.label, optionMaxTextLength) : data.label;

  var params = _objectSpread({
    isMulti: isMulti,
    isSelected: isSelected,
    isDisabled: isDisabled,
    colorTheme: colorTheme,
    onClickMultiple: onClickMultiple,
    label: label,
    optionCheckColor: optionCheckColor,
    classes: classes
  }, !optionMaxTextLength ? {
    isEllipsis: optionEllipsis && !isAutoOptionHeight
  } : {});

  var getRef = useCallback(function (optionRef) {
    if (innerRef) {
      if (typeof innerRef === 'function') {
        innerRef(optionRef);
      } else {
        // eslint-disable-next-line no-param-reassign
        innerRef.current = optionRef;
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);
  return /*#__PURE__*/React.createElement("div", {
    ref: optionWrapperRef,
    className: cx('OptionContainer')
  }, /*#__PURE__*/React.createElement(Component, _extends({}, isMobile ? _objectSpread(_objectSpread({}, touchEvents), {}, {
    ref: getRef
  }) : _objectSpread(_objectSpread({}, props), {}, {
    innerRef: getRef
  }), {
    className: cx('Option', {
      Option_focused: isFocused,
      Option_disabled: isDisabled,
      Option_selected: isSelected,
      Option_suggestion: data.left || data.right,
      Option_autoHeight: isAutoOptionHeight,
      Option_last: data.value === (options[options.length - 1] || {}).value
    }, classes.option)
  }), isMobile ? renderMobileOption(data, params) : renderDesktopOption(data, params)));
};
Option.propTypes = process.env.NODE_ENV !== "production" ? {
  data: valuePropType,
  options: arrayOf(valuePropType),
  innerRef: func,
  // eslint-disable-next-line react/boolean-prop-naming
  isDisabled: bool,
  // eslint-disable-next-line react/boolean-prop-naming
  isFocused: bool,
  // eslint-disable-next-line react/boolean-prop-naming
  isMulti: bool,
  // eslint-disable-next-line react/boolean-prop-naming
  isSelected: bool,
  selectOption: func,
  selectProps: selectPropsPropTypes
} : {};